import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/useTranslation";
import { Plus } from "lucide-react";
import { useScriptVersions, useSetCurrentVersion, useUpdateVersion } from "../hooks";
import { CreateVersionForm } from "./CreateVersionForm";
import { EditVersionForm } from "./EditVersionForm";
import { VersionCard } from "./VersionCard";
import { useToast } from "@/hooks/use-toast";
import { logError } from "@/utils/errors";
import { startSpan } from "@/utils/sentry";
import type { Script } from "@/features/dashboard/types";
import type { ScriptVersion } from "../types";

interface VersionControlPanelProps {
  script: Script;
  open: boolean;
  onClose: () => void;
  formatDate: (date: string) => string;
  onRefresh?: () => Promise<void>;
}

export const VersionControlPanel = ({
  script,
  open,
  onClose,
  formatDate,
  onRefresh,
}: VersionControlPanelProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ScriptVersion | null>(null);

  const { data: versions, isLoading } = useScriptVersions(script.id);
  const { mutateAsync: setCurrentVersion } = useSetCurrentVersion();
  const { mutateAsync: updateVersion } = useUpdateVersion();

  const handleSetCurrent = async (versionId: string) => {
    try {
      await startSpan(
        {
          op: "neon.update",
          name: "scripts.setCurrentVersion",
          attributes: { scriptId: script.id, versionId },
        },
        () => setCurrentVersion({ scriptId: script.id, versionId })
      );
      toast({
        title: t("versions.version_updated"),
      });
      // Refresh parent data
      await onRefresh?.();
    } catch (error) {
      logError(error, {
        feature: "versions",
        action: "setCurrentVersion",
        scriptId: script.id,
        versionId,
      });
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleArchive = async (versionId: string) => {
    try {
      await startSpan(
        {
          op: "neon.update",
          name: "script_versions.archive",
          attributes: { scriptId: script.id, versionId },
        },
        () => updateVersion({ versionId, isActive: false })
      );
      toast({
        title: t("versions.version_updated"),
      });
      // Refresh parent data
      await onRefresh?.();
    } catch (error) {
      logError(error, {
        feature: "versions",
        action: "archiveVersion",
        scriptId: script.id,
        versionId,
      });
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (version: ScriptVersion) => {
    setEditingVersion(version);
    setShowCreateForm(false);
  };

  const handleEditSuccess = async () => {
    setEditingVersion(null);
    await onRefresh?.();
  };

  const handleViewScript = (url?: string | null) => {
    if (!url) {
      toast({
        title: t("versions.script_unavailable_title", { defaultValue: "No script available" }),
        description: t("versions.script_unavailable_description", {
          defaultValue: "Upload a script file or add a link before opening this version.",
        }),
        variant: "destructive",
      });
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCreateSuccess = async () => {
    setShowCreateForm(false);
    // Refresh parent data
    await onRefresh?.();
  };

  const currentVersionNumber = versions?.find(
    (v) => v.id === script.current_version_id
  )?.version_number;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="left" className="w-full sm:max-w-lg overflow-y-auto bg-background/95 backdrop-blur-xl border-r border-border/60 shadow-2xl">
        <SheetHeader>
          <SheetTitle>
            {t("versions.title")}: {script.movie_name}
          </SheetTitle>
          <SheetDescription>
            {t("versions.subtitle", { count: versions?.length || 0 })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Edit Version Section */}
          {editingVersion ? (
            <div className="rounded-lg border border-border/60 bg-card/50 p-4 space-y-4">
              <h3 className="font-semibold">
                {t("versions.edit")} - v{editingVersion.version_number}
              </h3>
              <EditVersionForm
                version={editingVersion}
                onSuccess={handleEditSuccess}
                onCancel={() => setEditingVersion(null)}
              />
            </div>
          ) : (
            <>
              {/* Create New Version Section */}
              <div className="space-y-4">
                {!showCreateForm ? (
                  <Button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t("versions.create_new")}
                  </Button>
                ) : (
                  <div className="rounded-lg border border-border/60 bg-card/50 p-4 space-y-4">
                    <h3 className="font-semibold">{t("versions.create_new")}</h3>
                    <CreateVersionForm
                      scriptId={script.id}
                      onSuccess={handleCreateSuccess}
                      onCancel={() => setShowCreateForm(false)}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Version History */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Version History</h3>
              {currentVersionNumber && (
                <p className="text-sm text-muted-foreground">
                  {t("versions.new_forms_use", { version: currentVersionNumber })}
                </p>
              )}
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading versions...
              </div>
            ) : versions && versions.length > 0 ? (
              <div className="space-y-3">
                {versions.map((version) => (
                  <VersionCard
                    key={version.id}
                    version={version}
                    isCurrent={version.id === script.current_version_id}
                    formatDate={formatDate}
                    onSetCurrent={handleSetCurrent}
                    onEdit={handleEdit}
                    onArchive={handleArchive}
                    onViewScript={handleViewScript}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t("versions.no_versions")}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
